'use client'
import { Box, Flex, Group, Skeleton } from '@mantine/core'
import { useTranslations } from 'next-intl'
import { Suspense, useMemo } from 'react'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import Swiper from '@/components/Swiper'
import { useSsrContext } from '@/contexts/ssr'
import { isEEARegion } from '@/i18n/country'
import { useCryptoBasket, useYieldProducts } from '@/services/invest'
import { InvestCardType } from '@/types/invest'

import InvestCard from './InvestCard'

function InvestFallback() {
  return (
    <Box mt={80} style={{ overflow: 'hidden' }}>
      {/* Title and controller area */}
      <Flex justify="space-between" align="center" mb={16}>
        <Skeleton
          h={32}
          w={160}
          style={{ backgroundColor: 'var(--bg-surface-secondary)', borderRadius: 40 }}
        />
      </Flex>

      {/* Content cards area */}
      <Box style={{ overflow: 'hidden' }}>
        <Group gap="md" wrap="nowrap">
          {[0, 1, 2, 3].map(index => (
            <Skeleton
              key={index}
              h={240}
              w={190}
              style={{
                backgroundColor: 'var(--bg-surface-secondary)',
                borderRadius: 16,
                flexShrink: 0,
              }}
            />
          ))}
        </Group>
      </Box>
    </Box>
  )
}

function InvestContent({ locale }: { locale: string }) {
  const { coin } = useCoinContext()
  const { countryCode } = useSsrContext()

  const { data: basket } = useCryptoBasket(coin?.symbol || 'BTC')
  const { data: yieldProductsData } = useYieldProducts(coin?.symbol || 'BTC')
  const t = useTranslations('invest')

  const swiperList = useMemo(() => {
    const list = []

    // Add basket card
    if (basket?.crypto_baskets.length > 0) {
      const firstBasket = basket.crypto_baskets[0]
      list.push({
        type: InvestCardType.Basket,
        title: firstBasket.title,
        image: firstBasket.banner_url,
        updateTime: firstBasket.updated_at,
        tagIcons: firstBasket.coins,
        badge: t('basket.mostTraded'),
        link: 'https://web.crypto.com/signup',
      })
    }

    // Add yield product cards
    const yieldCards = [
      {
        type: InvestCardType.Stake,
        title: t('stake.title', { symbol: coin?.symbol }),
        buttonText: t('stake.buttonText', { symbol: coin?.symbol }),
        link: 'https://crypto.com/earn',
      },
      {
        type: InvestCardType.Earn,
        title: t('earn.title'),
        buttonText: t('earn.buttonText', { symbol: coin?.symbol }),
        link: 'https://crypto.com/earn/',
      },
      {
        type: InvestCardType.Recurring,
        title: t('recurring.title'),
        buttonText: t('recurring.buttonText'),
        image: '',
        link: 'https://web.crypto.com/signup',
      },
      {
        type: InvestCardType.Exchange,
        title: t('exchange.title'),
        buttonText: t('exchange.buttonText'),
        image: '',
        link: 'https://crypto.com/exchange',
      },
    ]

    yieldCards.forEach(({ type, title, buttonText, image, link }) => {
      // Only show Exchange card when isRestricted is false
      if (type === InvestCardType.Exchange) {
        if (isEEARegion(locale, countryCode)) return
        list.push({
          type,
          title,
          buttonText,
          image,
          link,
        })
        return
      }

      const data = yieldProductsData?.find(item => item.product_type === type)

      if (data || type === InvestCardType.Recurring) {
        const apr = data ? Number(data.apr) * 100 : null
        const finalTitle = apr
          ? `${title} ${apr}% p.a.${type === InvestCardType.Earn ? ` ${t('earn.withRegularPayouts')}` : ''}`
          : title

        list.push({
          type,
          title: finalTitle,
          image: image || data?.icon_url || '',
          buttonText,
          link,
        })
      }
    })

    return list
  }, [basket.crypto_baskets, t, coin?.symbol, yieldProductsData, locale, countryCode])

  if (swiperList.length === 0) {
    return null
  }

  return (
    <Box mt={80}>
      <Swiper
        title={t('title', { coinName: coin?.name })}
        swiperList={swiperList}
        renderSwiper={(item, idx) => <InvestCard key={idx} {...item} />}
      />
    </Box>
  )
}

function Invest({ locale }: { locale: string }) {
  return (
    <Suspense fallback={<InvestFallback />}>
      <InvestContent locale={locale} />
    </Suspense>
  )
}

export default SectionErrorBoundary.with(Invest)
